import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | failed
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (e) {}
      tries.current += 1;
      if (tries.current > 15) {
        setStatus("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen bg-app flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-white rounded-3xl card-shadow p-10 text-center">
        {status === "checking" && (
          <>
            <div className="w-12 h-12 rounded-full border-[3px] border-[var(--surface-accent)] border-t-[var(--brand)] animate-spin mx-auto" />
            <h1 className="font-serif text-2xl font-bold text-ink mt-6">Confirming your payment…</h1>
            <p className="text-ink-soft text-sm mt-2">Just a moment while we unlock your reset.</p>
          </>
        )}
        {status === "paid" && (
          <div className="animate-soft-scale">
            <div className="w-16 h-16 rounded-full bg-sage flex items-center justify-center mx-auto">
              <CheckCircle2 size={32} className="text-brand" />
            </div>
            <h1 className="font-serif text-3xl font-bold text-ink mt-6">You're all set</h1>
            <p className="text-ink-soft text-sm mt-2">
              Your 30 Day Menopause Reset is ready. Let's set it up for you.
            </p>
            <button
              data-testid="to-onboarding-btn"
              onClick={() => navigate("/onboarding")}
              className="mt-8 w-full bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-3.5 rounded-full transition-colors"
            >
              Continue to setup
            </button>
          </div>
        )}
        {status === "failed" && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto">
              <XCircle size={32} className="text-red-500" />
            </div>
            <h1 className="font-serif text-2xl font-bold text-ink mt-6">We couldn't confirm payment</h1>
            <p className="text-ink-soft text-sm mt-2">
              If you were charged, it may take a moment. You can refresh, or try again.
            </p>
            <button
              onClick={() => navigate("/checkout")}
              className="mt-8 w-full bg-brand text-white font-semibold py-3.5 rounded-full"
            >
              Back to checkout
            </button>
          </>
        )}
      </div>
    </div>
  );
}
